import React from 'react';
import { Download } from 'lucide-react';
import OrderCTA from '../components/OrderCTA';

// Import Fabric Images
import fabricCotton from '../assets/fabric_cotton.webp';
import fabricJersey from '../assets/fabric_jersey.webp';
import fabricPolyester from '../assets/fabric_polyester.webp';
import fabricRibbed from '../assets/fabric_ribbed.webp';
import fabricFleece from '../assets/fabric_fleece.webp';

// Import Product Images
import productJerseySheet from '../assets/product_jersey_sheet.webp';
import productCottonSheet from '../assets/fitted.webp'; // Using existing asset as placeholder
import productBedsheet from '../assets/bedd.webp'; // Using existing asset as placeholder
import productladieswear from '../assets/ladieswear.webp'; // Using existing asset as placeholder (generic)
import productPillow from '../assets/pillowcase.webp'; // Using existing asset as placeholder
import productTowel from '../assets/towel.webp'; // Using existing asset as placeholder (generic)
import collectionBg from '../assets/collectionbg.webp';

const fabrics = [
    {
        id: 1,
        name: 'Cotton Fabrics',
        description: 'Soft, breathable, and versatile, ideal for apparel, home textiles, and accessories.',
        image: fabricCotton
    },
    {
        id: 2,
        name: 'Jersey Fabrics',
        description: 'Stretchy and comfortable, ideal for casual and athletic wear.',
        image: fabricJersey
    },
    {
        id: 3,
        name: 'Polyester Fabrics',
        description: 'Durable and wrinkle-resistant, suitable for a wide range of applications.',
        image: fabricPolyester
    },
    {
        id: 4,
        name: 'Ribbed Fabrics',
        description: 'Textured and flexible, providing a snug fit.',
        image: fabricRibbed
    },
    {
        id: 5,
        name: 'Fleece Fabrics',
        description: 'Soft and insulating, perfect for colder climates.',
        image: fabricFleece
    }
];

const finishedProducts = [
    {
        id: 1,
        name: 'Jersey Fitted Sheet',
        features: [
            'Soft & stretchable jersey cotton',
            'Perfect fitted grip with elastic corners',
            'Wrinkle-free & breathable fabric',
            'Comfortable for year-round use',
            'Easy to wash & maintain',
            'Sizes: Single, Double, King'
        ],
        image: productJerseySheet
    },
    {
        id: 2,
        name: 'Cotton Fitted Sheet',
        features: [
            'Made from 100% pure cotton',
            'Soft, breathable & comfortable',
            'Perfect fitted grip with elastic edges',
            'Durable & easy to wash'
        ],
        image: productCottonSheet
    },
    {
        id: 3,
        name: 'Bed Sheet',
        features: [
            'Ideal for kids & elderly care',
            'Wrinkle-free & breathable fabric',
            'Easy to wash & maintain',
            'Daily protection',
            'Sizes: Single, Double, King'
        ],
        image: productBedsheet
    },
    {
        id: 4,
        name: 'Pillow Case',
        features: [
            'Made from 100% pure cotton',
            'Soft, breathable & comfortable',
            'Durable & easy to wash'
        ],
        image: productPillow
    },
    {
        id: 5,
        name: 'Towel',
        features: [
            'Made from 100% pure cotton',
            'Soft, breathable & comfortable',
            'Durable & easy to wash'
        ],
        image: productTowel
    },
    {
        id: 6,
        name: 'Women’s Sleep & Lounge Wear',
        features: [
            'Soft & breathable fabric',
            'Comfortable, relaxed fit',
            'Ideal for sleep & lounge wear',
            'Easy to wear & maintain'
        ],
        image: productladieswear
    }
];

const Collection = () => {
    return (
        <div className="bg-gray-50 min-h-screen">
            <section
                className="relative text-white py-40 px-4 sm:px-6 lg:px-8 text-center overflow-hidden bg-cover bg-bottom min-h-[80vh] flex flex-col justify-center"
                style={{ backgroundImage: `url(${collectionBg})` }}
            >
                <div className="absolute inset-0 bg-black opacity-50"></div>
                <div className="relative z-10 max-w-4xl mx-auto">
                    <h1 className="text-5xl md:text-6xl font-bold font-serif mb-6 text-secondary">Our Collection</h1>
                    <p className="text-xl md:text-2xl text-green-50 max-w-3xl mx-auto px-4 font-light">
                        Explore our range of premium fabrics and expertly crafted finished products.
                        Custom manufactured to your exact specifications.
                    </p>
                    <button
                        onClick={() => document.getElementById('products-section').scrollIntoView({ behavior: 'smooth' })}
                        className="mt-8 bg-primary text-white px-8 py-3 rounded-full font-bold hover:bg-primary-100 transition shadow-lg inline-block"
                    >
                        View All Products
                    </button>
                </div>
            </section>

            <div id="products-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

                {/* Catalog Download */}
                <div className="mb-20 text-center">
                    <a
                        href="/catlog.pdf"
                        target="_blank"
                        className="inline-flex items-center gap-2 bg-dark text-white px-10 py-4 rounded-full font-bold hover:bg-gray-800 transition shadow-xl transform hover:scale-105"
                    >
                        <Download size={20} />
                        View Full Catalog (PDF)
                    </a>
                </div>



                {/* Finished Products Section */}
                <div>
                    <div className="flex items-center gap-4 mb-12">
                        <div className="h-px bg-gray-300 flex-1"></div>
                        <h2 className="text-3xl font-bold text-primary font-serif uppercase tracking-widest">Premium Finished Products</h2>
                        <div className="h-px bg-gray-300 flex-1"></div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {finishedProducts.map((product) => (
                            <div key={product.id} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition duration-500 flex flex-col md:flex-row group h-full">
                                <div className="md:w-1/2 overflow-hidden h-auto aspect-[4/3] md:aspect-auto md:h-auto">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transform group-hover:scale-105 transition duration-700"
                                    />
                                </div>
                                <div className="p-8 md:w-1/2 flex flex-col justify-center">
                                    <h3 className="text-2xl font-bold text-dark mb-4 group-hover:text-primary transition-colors">{product.name}</h3>
                                    <ul className="space-y-2">
                                        {product.features.map((feature, index) => (
                                            <li key={index} className="flex items-start gap-2 text-gray-600 text-sm">
                                                <span className="mt-1.5 w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0"></span>
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Fabrics Section */}
                <div className="mb-24 mt-16">
                    <div className="flex items-center gap-4 mb-12">
                        <div className="h-px bg-gray-300 flex-1"></div>
                        <h2 className="text-3xl font-bold text-primary font-serif uppercase tracking-widest">Textile Materials</h2>
                        <div className="h-px bg-gray-300 flex-1"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {fabrics.map((fabric) => (
                            <div key={fabric.id} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition duration-500 group border border-gray-100">
                                <div className="h-auto aspect-[4/3] overflow-hidden relative">
                                    <img
                                        src={fabric.image}
                                        alt={fabric.name}
                                        className="w-full h-full object-cover transform group-hover:scale-110 transition duration-700"
                                    />
                                    <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition duration-500"></div>
                                </div>
                                <div className="p-8">
                                    <h3 className="text-2xl font-bold text-primary mb-3 font-serif">{fabric.name}</h3>
                                    <p className="text-gray-600 leading-relaxed">{fabric.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <OrderCTA title="Everything Custom Made. Just for You." />
        </div>
    );
};

export default Collection;
